import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import useOrganizationInfo from './useOrganizationInfo';

interface UseTrialStatusReturn {
  isTrialExpired: boolean;
  isTrialExpiringSoon: boolean;
  trialEndsAt: Date | null;
  daysUntilExpiry: number | null;
  checkTrialStatus: () => boolean;
  showTrialExpiredModal: boolean;
  showTrialExpiringModal: boolean;
  setShowTrialExpiredModal: (show: boolean) => void;
  setShowTrialExpiringModal: (show: boolean) => void;
}

export function useTrialStatus(): UseTrialStatusReturn {
  const { user } = useAuth();
  const { orgInfo } = useOrganizationInfo();
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  const [showTrialExpiringModal, setShowTrialExpiringModal] = useState(false);
  
  const checkTrialStatus = useCallback(() => {
    if (!orgInfo) return false;
    
    // Check if organization is on trial plan and trial has expired
    const isOnTrial = orgInfo.plan === 'TRIAL';
    const trialExpired = orgInfo.trialEndsAt && new Date(orgInfo.trialEndsAt) < new Date();
    
    return isOnTrial && trialExpired;
  }, [orgInfo]);
  
  const isTrialExpired = checkTrialStatus();
  const trialEndsAt = orgInfo?.trialEndsAt ? new Date(orgInfo.trialEndsAt) : null;
  
  // Calculate days until expiry
  const daysUntilExpiry = trialEndsAt && !isTrialExpired ? 
    Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
  
  // Check if trial is expiring soon (≤ 5 days but not expired)
  const isTrialExpiringSoon = orgInfo?.plan === 'TRIAL' && 
    !isTrialExpired && 
    daysUntilExpiry !== null && 
    daysUntilExpiry <= 5 && 
    daysUntilExpiry > 0;
  
  // Check trial status after org info loads
  useEffect(() => {
    if (orgInfo && !sessionStorage.getItem('trial_status_checked')) {
      if (isTrialExpired) {
        // Show expired modal
        setShowTrialExpiredModal(true);
        sessionStorage.setItem('trial_status_checked', 'true');
      } else if (isTrialExpiringSoon) {
        // Show expiring soon modal
        setShowTrialExpiringModal(true);
        sessionStorage.setItem('trial_status_checked', 'true');
      }
    }
  }, [orgInfo, isTrialExpired, isTrialExpiringSoon]);
  
  return {
    isTrialExpired,
    isTrialExpiringSoon,
    trialEndsAt,
    daysUntilExpiry,
    checkTrialStatus,
    showTrialExpiredModal,
    showTrialExpiringModal,
    setShowTrialExpiredModal,
    setShowTrialExpiringModal
  };
}

export default useTrialStatus;